import React, { createContext, ReactNode, useContext, useState, useCallback, useMemo } from 'react';
import Modal from '../components/Modal';
import Toasts from '../components/Toasts';

type ShowModalFn = (title: string, message: string, onConfirm?: () => void) => void;
type ToastType = 'success' | 'error' | 'info';
type ShowToastFn = (type: ToastType, message: string, duration?: number) => void;

interface UIContextType {
  showModal: ShowModalFn;
  showToast: ShowToastFn;
}

const UIContext = createContext<UIContextType>({ showModal: () => {}, showToast: () => {} });

export const useUI = () => useContext(UIContext);

let idCounter = 0;

export const UIProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [onConfirm, setOnConfirm] = useState<(() => void) | undefined>(undefined);

  const [toasts, setToasts] = useState<Array<{ id: number; type: ToastType; message: string }>>([]);

  const showModal: ShowModalFn = useCallback((t, m, cb) => {
    setTitle(t);
    setMessage(m);
    setOnConfirm(() => cb);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setOnConfirm(undefined);
  }, []);

  const handleConfirm = useCallback(() => {
    if (onConfirm) onConfirm();
    handleClose();
  }, [onConfirm, handleClose]);

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast: ShowToastFn = useCallback((type, msg, duration = 3500) => {
    const id = ++idCounter;
    setToasts(prev => [{ id, type, message: msg }, ...prev]);
    setTimeout(() => removeToast(id), duration);
  }, [removeToast]);

  const value = useMemo(() => ({ showModal, showToast }), [showModal, showToast]);

  return (
    <UIContext.Provider value={value}>
      {children}
      <Modal isOpen={open} onClose={handleClose} title={title}>
        <div>
          <p className="text-gray-700">{message}</p>
          <div className="mt-6 flex justify-end">
            <button onClick={handleClose} className="mr-3 px-4 py-2 rounded-md bg-gray-200">Cancelar</button>
            <button onClick={handleConfirm} className="px-4 py-2 rounded-md bg-blue-600 text-white">Aceptar</button>
          </div>
        </div>
      </Modal>
      <Toasts toasts={toasts} onRemove={removeToast} />
    </UIContext.Provider>
  );
};

export default UIContext;
